import { useBoothEventStore, type PrintQueueResult } from '@renderer/stores/boothEventStore'
import { useFeatureFlagsStore } from '@renderer/stores/featureFlagsStore'
import { useSessionStateStore } from '@renderer/stores/sessionState'

/**
 * The renderer half of the print-queue watcher (main/utils/printQueueWatch).
 *
 * The shell watches the Windows queue after each print and reports how the job
 * ended. Here every outcome goes into the session's activity log (so the first
 * weeks show how long a strip takes to leave the queue on each printer, and
 * what the driver says when it doesn't), and a stuck or errored job becomes a
 * printer_incident the server pages on.
 *
 * Gated by the `print_job_watch` flag (kill switch, per account) and by the
 * shell: shells without the watcher have no `onPrintJobOutcome`.
 */

export const PRINT_JOB_WATCH_FLAG = 'print_job_watch'

let installed = false

/** Whether this print should be watched: flag on and a shell that can watch. */
export function shouldWatchPrintQueue(): boolean {
  if (typeof window === 'undefined' || typeof window.api?.onPrintJobOutcome !== 'function') return false
  return Boolean(useFeatureFlagsStore.getState().flags[PRINT_JOB_WATCH_FLAG])
}

const MESSAGE: Record<PrintQueueResult['outcome'], string> = {
  printed: 'Print job left the printer queue',
  stuck: 'Print job stuck in the printer queue',
  error: 'Printer reported a problem with the print job',
  unknown: 'Printer queue could not be read'
}

export function handlePrintQueueOutcome(result: PrintQueueResult): void {
  try {
    const problem = result.outcome === 'stuck' || result.outcome === 'error'
    useSessionStateStore.getState().logActivity(MESSAGE[result.outcome], {
      level: problem ? 'warning' : 'info',
      printerName: result.printerName,
      reason: result.reason,
      waitedMs: result.waitedMs,
      sessions: result.contexts.map((c) => c.sessionId).filter(Boolean),
      spooler: result.last
    },
    undefined,
    // On the session that printed, even if the booth has moved on to the next guest.
    result.contexts[result.contexts.length - 1]?.sessionId)
  } catch (error) {
    console.warn('[print-queue] could not log outcome', error)
  }
  try {
    useBoothEventStore.getState().reportPrintQueueOutcome(result)
  } catch (error) {
    console.warn('[print-queue] could not report outcome', error)
  }
}

/** Subscribe once, the first time a watched print is sent. */
export function installPrintQueueListener(): void {
  if (installed || typeof window === 'undefined' || typeof window.api?.onPrintJobOutcome !== 'function') return
  installed = true
  window.api.onPrintJobOutcome((result) => handlePrintQueueOutcome(result as PrintQueueResult))
}
